import CustomButton from 'components/Buttons/CustomButton'
import PMBModalTraceability from 'components/PMBModalTraceability'
import { useState, useEffect, useCallback } from 'react'
import QRCode from 'react-qr-code'
import { Printer, Smartphone, Link, ExternalLink } from 'react-feather'
import { Card, CardBody, Alert, Spinner } from 'reactstrap'
import FormatProcessor from 'utils/FormatProcessor'
import { thermalPrinter } from 'utils/thermalPrinter'
import { renderQrWithLogoToCanvas } from 'utils/qrLogoHelper'
import pmlogo from 'assets/images/pmlogo.png'
import { useRef } from 'react'

export default function IDStockModal({ isOpen, onClose, selectedStock }) {
  const [selectedProductId, setSelectedProductId] = useState(null)
  const [isOpenModalTraceability, setIsOpenModalTraceability] = useState(false)
  const [isConnected, setIsConnected] = useState(thermalPrinter.isConnected)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const qrRef = useRef(null)
  const logoRef = useRef(null)

  const inventoryUrl = process.env.REACT_APP_INVENTORY_URL
  let url = `${inventoryUrl}?productId=${selectedStock?.traceCode}`

  const onOpenCommodityOnWeb = () => {
    window.open(url + '&dt=0', '_blank')
  }

  const handleOpenCloseModalTraceability = (productId = null) => {
    setSelectedProductId(productId)
    setIsOpenModalTraceability((prev) => !prev)
  }

  const handleToggleConnect = async () => {
    if (isConnected) {
      await thermalPrinter.disconnect()
      setIsConnected(false)
    } else {
      setIsConnecting(true)
      try {
        const success = await thermalPrinter.connect()
        if (success) setIsConnected(true)
      } catch (err) {
        console.error('Connection failed:', err)
      } finally {
        setIsConnecting(false)
      }
    }
  }

  const handlePrint = async () => {
    if (!isConnected) return

    setIsPrinting(true)
    try {
      const title = selectedStock?.nameItem || selectedStock?.name || selectedStock?.itemName || 'Tanpa Nama'
      const subtitle = `ID: ${selectedStock?.id || '-'}`
      
      const svgElement = qrRef.current?.querySelector('svg')
      const logoElement = logoRef.current
      
      const canvas = await renderQrWithLogoToCanvas(svgElement, logoElement, 384)

      if (canvas) {
        await thermalPrinter.printQrAsImage(title, subtitle, canvas)
      } else {
        // Fallback
        const qrValue = FormatProcessor.generate('searchInventory', {
          id: selectedStock?.id,
          name: title,
          gtin: selectedStock?.gtin || '',
          expiry: selectedStock?.expiry || '',
          traceCode: selectedStock?.traceCode || '',
          url,
          productDescription: title,
        })
        await thermalPrinter.printQrItem(title, subtitle, qrValue)
      }
    } catch (err) {
      console.error('Print failed:', err)
    } finally {
      setIsPrinting(false)
    }
  }

  useEffect(() => {
    // Sync connection state
    setIsConnected(thermalPrinter.isConnected)
    
    // Auto connect if possible
    if (!thermalPrinter.isConnected) {
      thermalPrinter.autoConnect().then(success => {
        if (success) setIsConnected(true)
      })
    }
  }, [isOpen])

  return (
    <>
      <PMBModalTraceability
        isOpen={isOpenModalTraceability}
        toggle={() => handleOpenCloseModalTraceability()}
        productId={selectedProductId}
      />

      <div className="px-3 pb-4">
        <Card className="border-0 shadow-sm overflow-hidden mb-4 mt-2" style={{ borderRadius: '16px', border: '1px solid #F2F4F7' }}>
          <CardBody className="p-4 text-center">
            <div ref={qrRef} className="mb-4 d-inline-block p-3 bg-white shadow-sm rounded-4" style={{ border: '1px solid #F2F4F7' }}>
              <QRCode
                size={200}
                value={FormatProcessor.generate('searchInventory', {
                  id: selectedStock?.id,
                  name: selectedStock?.nameItem || selectedStock?.name || selectedStock?.itemName || '',
                  gtin: selectedStock?.gtin || '',
                  expiry: selectedStock?.expiry || '',
                  traceCode: selectedStock?.traceCode || '',
                  url,
                  productDescription: selectedStock?.nameItem || selectedStock?.name || '',
                })}
              />
            </div>
            {/* Hidden logo for canvas rendering */}
            <img ref={logoRef} src={pmlogo} alt="logo-hidden" style={{ display: 'none' }} />

            <div className="mb-4">
              <h5 className="fw-bold text-dark mb-1">
                {selectedStock?.nameItem || selectedStock?.name || selectedStock?.itemName || 'Tanpa Nama'}
              </h5>
              <p className="text-muted small mb-0">ID: {selectedStock?.id || '-'}</p>
            </div>

            <Alert color="primary" className="border-0 mb-0 py-2 px-3 d-flex align-items-center justify-content-center gap-2" style={{ backgroundColor: '#F0F9FF', borderRadius: '12px' }}>
              <Smartphone size={16} style={{ color: '#0086C9' }} />
              <span style={{ fontSize: '0.8rem', color: '#0086C9', fontWeight: '500' }}>
                Scan QR dari aplikasi mobile Anda
              </span>
            </Alert>
          </CardBody>
        </Card>

        <div className="d-flex flex-column gap-3">
          {/* Printer Actions */}
          {!isConnected ? (
            <CustomButton
              className="py-2 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
              style={{
                backgroundColor: '#FDB022',
                border: 'none',
                color: 'white',
                borderRadius: '10px',
              }}
              onClick={handleToggleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Spinner size="sm" /> Mencari printer...
                </>
              ) : (
                <>
                  <Printer size={18} /> Hubungkan Printer
                </>
              )}
            </CustomButton>
          ) : (
            <CustomButton
              className="py-2 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
              style={{
                backgroundColor: '#12B76A',
                border: 'none',
                color: 'white',
                borderRadius: '10px',
              }}
              onClick={handlePrint}
              disabled={isPrinting}
            >
              {isPrinting ? (
                <>
                  <Spinner size="sm" /> Mencetak...
                </>
              ) : (
                <>
                  <Printer size={18} /> Print QR Code
                </>
              )}
            </CustomButton>
          )}

          {/* Public Link Action */}
          <div className="d-flex gap-2">
            <CustomButton
              outline
              color="secondary"
              className="flex-fill py-2 d-flex align-items-center justify-content-center gap-2 fw-medium"
              style={{
                borderRadius: '10px',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontSize: '0.85rem',
              }}
              onClick={onOpenCommodityOnWeb}
            >
              <ExternalLink size={15} />
              Buka Keterlacakan Publik
            </CustomButton>
            <CustomButton
              outline
              color="secondary"
              className="px-3 py-2 d-flex align-items-center justify-content-center"
              style={{
                borderRadius: '10px',
                borderColor: '#D0D5DD',
                color: '#344054',
              }}
              onClick={() => {
                navigator.clipboard.writeText(url)
                alert('Link disalin!')
              }}
              title="Salin Link"
            >
              <Link size={16} />
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  )
}
